import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, X, Clock, Calendar, Building2, CheckCircle2 } from 'lucide-react';
import CandidateLayout from '../components/CandidateLayout';
import { applicationApiService, Application } from '../services/applicationApi';
import { useAuthProtection } from '../hooks/useAuthProtection';
import Modal from '../../shared/components/modern/Modal';

type TabType = 'apply_status' | 'offered_job' | 'saved_job';
type FilterType = 'all' | 'applied' | 'interview' | 'offered' | 'rejected';
type SortType = 'newest' | 'oldest';

interface ApplicationWithJob extends Application {
  // Extended application with potential job details
}

interface StageInfo {
  name: string;
  displayName: string;
  color: string;
  completed: boolean;
  timestamp?: string;
  description?: string;
}

/**
 * Applications Page
 * Displays all job applications for the candidate with filters and tabs
 * Protected Route: Only accessible to authenticated candidates
 */
const Applications: React.FC = () => {
  // Protect route - redirect to login if not authenticated
  useAuthProtection();

  const [applications, setApplications] = useState<ApplicationWithJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('apply_status');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [sortBy, setSortBy] = useState<SortType>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  // Application details modal state
  const [selectedApplication, setSelectedApplication] = useState<ApplicationWithJob | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const itemsPerPage = 20;

  // Fetch applications with real API
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setIsLoading(true);

        // Only fetch for apply_status tab - saved jobs will use getSavedJobs()
        if (activeTab === 'saved_job') {
          // TODO: Implement saved jobs fetching
          setApplications([]);
          setIsLoading(false);
          return;
        }

        if (activeTab === 'offered_job') {
          // Fetch applications with 'offered' stage
          const params = {
            page: currentPage,
            limit: itemsPerPage,
            stage: 'offered',
          };

          const response = await applicationApiService.getMyApplications(params);
          const applicationsData = response.payload.data;
          const pagination = response.payload.pagination;

          setTotalPages(pagination.totalPages);

          const mappedApplications = applicationsData.map(app => ({
            ...app,
            job_id: app.jobId,
            candidate_id: app.candidateId,
            current_stage: 'offered',
            resume_url: app.resumeUrl,
            cover_letter: app.coverLetter,
            applied_at: app.appliedAt,
            updated_at: app.updatedAt,
            job_title: app.jobTitle,
            company_name: app.companyName,
          } as ApplicationWithJob));

          setApplications(mappedApplications);
          setIsLoading(false);
          return;
        }

        // Build params for API - stage values match backend directly
        const params = {
          page: currentPage,
          limit: itemsPerPage,
          stage: activeFilter === 'all' ? undefined : activeFilter,
        };

        const response = await applicationApiService.getMyApplications(params);

        // Response structure: { payload: { data: [], pagination: {} }, status: {} }
        const applicationsData = response.payload.data;
        const pagination = response.payload.pagination;

        setTotalPages(pagination.totalPages);

        // Applications already come enriched with job details from backend
        // Map to frontend structure
        const mappedApplications = applicationsData.map(app => ({
          ...app,
          job_id: app.jobId,
          candidate_id: app.candidateId,
          current_stage: mapStatusToStage(app.currentStage || app.status),
          resume_url: app.resumeUrl,
          cover_letter: app.coverLetter,
          applied_at: app.appliedAt,
          updated_at: app.updatedAt,
          job_title: app.jobTitle,
          company_name: app.companyName,
        } as ApplicationWithJob));

        setApplications(mappedApplications);
      } catch (error) {
        console.error('Error fetching applications:', error);
        setApplications([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplications();
  }, [currentPage, activeFilter, sortBy, activeTab]);

  // Map backend stage to frontend display - now uses currentStage directly
  const mapStatusToStage = (stage: string): string => {
    // Backend now returns 'applied', 'interview', 'offered', 'rejected' directly
    // Keep mapping for backward compatibility with any old status values
    const mapping: Record<string, string> = {
      'pending': 'applied',
      'reviewed': 'applied',
      'shortlisted': 'interview',
      'rejected': 'rejected',
      'hired': 'offered',
      'withdrawn': 'rejected',
    };
    return mapping[stage] || stage;
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'applied':
        return 'border-[#0B111E] text-[#0B111E]'; // Black border
      case 'interview':
        return 'border-[#00AEAE] text-[#00AEAE]'; // Teal border
      case 'offered':
        return 'border-[#00AC47] text-[#00AC47]'; // Green border
      case 'rejected':
        return 'border-[#DC0000] text-[#DC0000]'; // Red border
      default:
        return 'border-[#A5A5A5] text-[#A5A5A5]'; // Gray border
    }
  };

  const getStageLabel = (stage: string) => {
    switch (stage) {
      case 'applied':
        return 'Applied';
      case 'interview':
        return 'Interview';
      case 'offered':
        return 'Offer';
      case 'rejected':
        return 'Rejected';
      default:
        return stage;
    }
  };

  const getTimelineColor = (stage: string) => {
    switch (stage) {
      case 'applied':
        return '#0B111E';
      case 'interview':
        return '#00AEAE';
      case 'offered':
        return '#00AC47';
      case 'rejected':
        return '#DC0000';
      default:
        return '#757575';
    }
  };

  const getTimelineBgColor = (stage: string) => {
    switch (stage) {
      case 'applied':
        return 'bg-[#0B111E]';
      case 'interview':
        return 'bg-[#00AEAE]';
      case 'offered':
        return 'bg-[#00AC47]';
      case 'rejected':
        return 'bg-[#DC0000]';
      default:
        return 'bg-[#757575]';
    }
  };

  const buildTimeline = (app: ApplicationWithJob): StageInfo[] => {
    const stages: StageInfo[] = [];
    const currentStage = app.currentStage;

    // Define stage progression: Applied → Interview → Offer
    const stageOrder = ['applied', 'interview', 'offered'];
    const currentIndex = stageOrder.indexOf(currentStage);

    // Build timeline based on current stage
    stageOrder.forEach((stage, index) => {
      const isCompleted = index <= currentIndex;
      const stageInfo: StageInfo = {
        name: stage,
        displayName: getStageLabel(stage),
        color: getTimelineColor(stage),
        completed: isCompleted,
      };

      // Add timestamps (mock data - in real app, get from stage_history)
      if (isCompleted) {
        if (stage === 'applied') {
          stageInfo.timestamp = formatRelativeTime(app.appliedAt);
        } else if (stage === currentStage) {
          stageInfo.timestamp = formatRelativeTime(app.updatedAt);
        } else {
          stageInfo.timestamp = '3 days ago';
        }
      }

      stages.push(stageInfo);
    });

    return stages.reverse(); // Show newest first
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setActiveFilter('all');
    setCurrentPage(1);
  };

  const handleApplicationClick = (app: ApplicationWithJob) => {
    setSelectedApplication(app);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedApplication(null);
  };

  const renderPagination = () => {
    const pages = [];

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg"
      >
        <ChevronRight size={16} className="rotate-180" />
      </button>
    );

    // Page numbers
    [1, 2, 3].forEach(pageNum => {
      if (pageNum <= totalPages) {
        pages.push(
          <button
            key={pageNum}
            onClick={() => setCurrentPage(pageNum)}
            className={`w-8 h-8 flex items-center justify-center rounded-lg font-semibold ${
              currentPage === pageNum
                ? 'border border-[#015FA6] text-[#015FA6]'
                : 'hover:bg-gray-100'
            }`}
          >
            {pageNum}
          </button>
        );
      }
    });

    // Ellipsis
    if (totalPages > 5) {
      pages.push(<span key="ellipsis" className="px-2">...</span>);
    }

    // Last pages
    if (totalPages > 3) {
      [totalPages - 1, totalPages].forEach(pageNum => {
        if (pageNum > 3) {
          pages.push(
            <button
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                currentPage === pageNum
                  ? 'border border-[#015FA6] text-[#015FA6] font-semibold'
                  : 'hover:bg-gray-100'
              }`}
            >
              {pageNum}
            </button>
          );
        }
      });
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg"
      >
        <ChevronRight size={16} />
      </button>
    );

    return pages;
  };

  // Get progress steps for timeline visualization
  // Order: Applied → Interview → Offer → Rejected
  const getProgressSteps = (currentStage: string) => {
    const allStages = ['applied', 'interview', 'offered'];
    const currentIndex = allStages.indexOf(currentStage);

    // If rejected, show special timeline
    if (currentStage === 'rejected') {
      return allStages.map((stage) => ({
        name: stage,
        label: getStageLabel(stage),
        completed: false,
        current: false,
        rejected: true,
      }));
    }

    return allStages.map((stage, index) => ({
      name: stage,
      label: getStageLabel(stage),
      completed: index < currentIndex,
      current: index === currentIndex,
      rejected: false,
    }));
  };

  return (
    <CandidateLayout
      headerTitle="Applications"
      headerSubtitle="Updating your information will offer you the most relevant content"
    >
      {/* Content Area */}
      <div className="h-[calc(100vh-180px)] flex flex-col">
        {/* White Container with Tabs and Content */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#EDEDED] flex-1 flex flex-col relative overflow-hidden">
          {/* Tabs */}
          <div className="flex items-center gap-6 lg:gap-8 border-b border-[#EDEDED] mb-4 flex-shrink-0">
            <button
              onClick={() => handleTabChange('apply_status')}
              className={`pb-4 text-sm font-semibold relative ${
                activeTab === 'apply_status' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
              }`}
            >
              Apply status
              {activeTab === 'apply_status' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
            </button>
            <button
              onClick={() => handleTabChange('offered_job')}
              className={`pb-4 text-sm font-semibold relative ${
                activeTab === 'offered_job' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
              }`}
            >
              Offered job
              {activeTab === 'offered_job' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
            </button>
            <button
              onClick={() => handleTabChange('saved_job')}
              className={`pb-4 text-sm font-semibold relative ${
                activeTab === 'saved_job' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
              }`}
            >
              Saved job
              {activeTab === 'saved_job' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
            </button>
          </div>

          {/* Filter Bar - Only show for Apply Status tab */}
          {activeTab === 'apply_status' && (
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              {/* Filter Buttons - Order: Applied → Interview → Offer → Rejected */}
              <div className="flex gap-2 flex-wrap">
                {(['all', 'applied', 'interview', 'offered', 'rejected'] as FilterType[]).map((filter) => {
                  // Display "Offer" instead of "Offered" for better UX
                  const displayLabel = filter === 'offered' ? 'Offer' : filter.charAt(0).toUpperCase() + filter.slice(1);
                  return (
                    <button
                      key={filter}
                      onClick={() => {
                        setActiveFilter(filter);
                        setCurrentPage(1);
                      }}
                      className={`px-4 py-2 rounded-lg text-xs font-medium border transition-colors ${
                        activeFilter === filter
                          ? 'bg-[#015FA6] text-white border-[#015FA6]'
                          : 'bg-white text-[#757575] border-[#EDEDED] hover:border-[#CBCBCB]'
                      }`}
                    >
                      {displayLabel}
                    </button>
                  );
                })}
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-1.5 border border-[#EDEDED] rounded-lg text-xs font-medium text-[#515151] hover:bg-gray-50"
                >
                  Newest <ChevronDown size={14} />
                </button>

                {sortDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-white border border-[#EDEDED] rounded-lg shadow-lg z-10">
                    <button
                      onClick={() => {
                        setSortBy('newest');
                        setSortDropdownOpen(false);
                        setCurrentPage(1);
                      }}
                      className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                    >
                      Newest
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('oldest');
                        setSortDropdownOpen(false);
                        setCurrentPage(1);
                      }}
                      className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                    >
                      Oldest
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Applications List */}
          <div className="space-y-2 flex-1 overflow-y-auto pr-1">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#015FA6]"></div>
              </div>
            ) : applications.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center min-h-[200px]">
                <img
                  src="/empty_application.svg"
                  alt="No applications"
                  className="w-40 h-40 mb-4"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <p className="text-[#A5A5A5] text-sm font-medium">There is no applications here yet</p>
              </div>
            ) : (
              applications.map((app) => (
                  <div
                    key={app.id}
                    onClick={() => handleApplicationClick(app)}
                    className="flex items-center justify-between p-3 border border-[#EDEDED] rounded-xl hover:shadow-sm transition-shadow bg-white cursor-pointer group hover:border-[#015FA6]/30"
                  >
                    <div className="flex items-center gap-3">
                      {/* Logo */}
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg bg-[#015FA6] flex-shrink-0">
                        {(app.companyName || 'C').charAt(0)}
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-[#A5A5A5]">{app.companyName || 'Company'}</span>
                        <h3 className="text-sm font-bold text-[#0B111E]">{app.jobTitle || 'Position'}</h3>
                        <div className="mt-0.5">
                          <span className={`px-2 py-0.5 rounded border text-[10px] font-medium ${getStageColor(app.currentStage)}`}>
                            {getStageLabel(app.currentStage)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#A5A5A5] hidden sm:block">
                        {formatRelativeTime(app.appliedAt)}
                      </span>
                      <ChevronRight size={18} className="text-[#A5A5A5] group-hover:text-[#015FA6] transition-colors" />
                    </div>
                  </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {!isLoading && applications.length > 0 && (
            <div className="flex justify-center items-center gap-2 pt-4 text-sm text-[#757575] flex-shrink-0 border-t border-[#EDEDED] mt-2">
              {renderPagination()}
            </div>
          )}

        </div>
      </div>

      {/* Application Details Modal */}
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={closeDetailsModal}
        showCloseButton={false}
        maxWidth="max-w-2xl"
      >
        {selectedApplication && (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#015FA6] flex items-center justify-center text-white font-bold text-xl">
                  {(selectedApplication.companyName || 'C').charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#0B111E]">
                    {selectedApplication.jobTitle || 'Position'}
                  </h2>
                  <p className="text-sm text-[#757575] flex items-center gap-1">
                    <Building2 size={14} />
                    {selectedApplication.companyName || 'Company'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeDetailsModal}
                className="p-2 hover:bg-[#F1F3F4] rounded-lg transition-colors"
              >
                <X size={20} className="text-[#757575]" />
              </button>
            </div>

            {/* Application Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-[#F9F9F9] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#757575] mb-1">
                  <Calendar size={16} />
                  <span className="text-xs font-medium uppercase">Applied Date</span>
                </div>
                <p className="text-sm font-semibold text-[#0B111E]">
                  {formatDate(selectedApplication.appliedAt)}
                </p>
              </div>
              <div className="bg-[#F9F9F9] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#757575] mb-1">
                  <Clock size={16} />
                  <span className="text-xs font-medium uppercase">Last Updated</span>
                </div>
                <p className="text-sm font-semibold text-[#0B111E]">
                  {formatRelativeTime(selectedApplication.updatedAt)}
                </p>
              </div>
            </div>

            {/* Current Status */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#0B111E] mb-3">Current Status</h3>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-lg text-sm font-semibold border ${getStageColor(selectedApplication.currentStage)}`}>
                  {getStageLabel(selectedApplication.currentStage)}
                </span>
              </div>
            </div>

            {/* Application Progress Timeline */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#0B111E] mb-4">Application Progress</h3>

              {selectedApplication.currentStage === 'rejected' ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">
                    Unfortunately, your application was not selected to move forward. Don't be discouraged - keep applying to other opportunities!
                  </p>
                </div>
              ) : (
                <div className="relative">
                  {/* Progress Bar */}
                  <div className="flex items-center justify-between mb-2">
                    {getProgressSteps(selectedApplication.currentStage).map((step, index, arr) => (
                      <React.Fragment key={step.name}>
                        {/* Step Circle */}
                        <div className="flex flex-col items-center z-10">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                              step.completed
                                ? 'bg-[#00AC47] border-[#00AC47] text-white'
                                : step.current
                                ? `${getTimelineBgColor(step.name)} border-current text-white`
                                : 'bg-white border-[#EDEDED] text-[#A5A5A5]'
                            }`}
                          >
                            {step.completed ? (
                              <CheckCircle2 size={20} />
                            ) : (
                              <span className="text-xs font-bold">{index + 1}</span>
                            )}
                          </div>
                          <span
                            className={`text-xs mt-2 font-medium ${
                              step.completed || step.current ? 'text-[#0B111E]' : 'text-[#A5A5A5]'
                            }`}
                          >
                            {step.label}
                          </span>
                        </div>

                        {/* Connector Line */}
                        {index < arr.length - 1 && (
                          <div className="flex-1 h-1 mx-2 -mt-6">
                            <div
                              className={`h-full rounded-full transition-all ${
                                step.completed ? 'bg-[#00AC47]' : 'bg-[#EDEDED]'
                              }`}
                            />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Timeline History */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#0B111E] mb-3">Timeline</h3>
              <div className="space-y-3">
                {buildTimeline(selectedApplication).filter(s => s.completed).map((stage, index) => (
                  <div key={stage.name} className="flex items-start gap-3">
                    <div
                      className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                      style={{ backgroundColor: stage.color }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[#0B111E]">{stage.displayName}</span>
                        <span className="text-xs text-[#757575]">{stage.timestamp}</span>
                      </div>
                      {stage.description && (
                        <p className="text-xs text-[#757575] mt-0.5">{stage.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Close Button */}
            <div className="mt-6 pt-4 border-t border-[#EDEDED]">
              <button
                onClick={closeDetailsModal}
                className="w-full py-3 px-4 border border-[#EDEDED] text-[#515151] rounded-lg font-medium hover:bg-[#F9F9F9] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </CandidateLayout>
  );
};

export default Applications;
